import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { SWRConfig } from 'swr';
import { EmptyState } from '@kollektive/design-system';
import { Layout, Settings, FileQuestion } from 'lucide-react';
import { TenantProvider, AuthProvider } from '@/context';
import { TenantGate } from '@/components/TenantGate';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { AdminRoute } from '@/components/AdminRoute';
import { AppLayout } from '@/components/AppLayout';
import { LoginPage } from '@/routes/LoginPage';
import { AuthCallback } from '@/routes/AuthCallback';
import { RegisterPage } from '@/routes/RegisterPage';
import { RegisterSetupPage } from '@/routes/RegisterSetupPage';
import { AcceptInvitationPage } from '@/routes/AcceptInvitationPage';
import { TokensPage } from '@/routes/TokensPage';
import { ConnectionsPage } from '@/routes/ConnectionsPage';
import { TeamPage } from '@/routes/TeamPage';
import { isRegistrationSubdomain } from '@/lib/tenant';
import styles from './App.module.css';

function DashboardPage() {
  return (
    <div className={styles.page}>
      <EmptyState
        icon={<Layout size={48} />}
        title="Dashboard"
        description="Dashboard coming soon"
      />
    </div>
  );
}


function SettingsPage() {
  return (
    <div className={styles.page}>
      <EmptyState
        icon={<Settings size={48} />}
        title="Settings"
        description="Settings coming soon"
      />
    </div>
  );
}

function NotFoundPage() {
  return (
    <div className={styles.notFound}>
      <EmptyState
        variant="error"
        icon={<FileQuestion size={48} />}
        title="404"
        description="Page not found"
      />
    </div>
  );
}

// Global SWR configuration
const swrConfig = {
  revalidateOnFocus: true,
  dedupingInterval: 2000,
  errorRetryCount: 3,
};

/**
 * Registration-only app for app.kollektive.ai subdomain.
 * No tenant context required - just registration flow.
 */
function RegistrationApp() {
  return (
    <SWRConfig value={swrConfig}>
      <BrowserRouter>
        <Routes>
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/register/setup" element={<RegisterSetupPage />} />
          {/* All other routes redirect to registration */}
          <Route path="*" element={<Navigate to="/register" replace />} />
        </Routes>
      </BrowserRouter>
    </SWRConfig>
  );
}

/**
 * Main tenant app for {tenant}.kollektive.ai subdomains.
 * Requires tenant context for most routes.
 */
function TenantApp() {
  return (
    <SWRConfig value={swrConfig}>
      <TenantProvider>
        <BrowserRouter>
          <Routes>
            {/* Public routes - accessible without tenant validation */}
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/register/setup" element={<RegisterSetupPage />} />
            <Route path="/invitations/accept" element={<AuthProvider><AcceptInvitationPage /></AuthProvider>} />

            {/* Tenant-gated routes */}
            <Route
              path="/*"
              element={
                <TenantGate>
                  <AuthProvider>
                    <Routes>
                      {/* Public route within tenant context */}
                      <Route path="/login" element={<LoginPage />} />

                      {/* Protected routes - require authentication */}
                      <Route
                        path="/"
                        element={<Navigate to="/dashboard" replace />}
                      />
                      <Route
                        path="/dashboard"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <DashboardPage />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/tokens"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <TokensPage />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/connections"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <ConnectionsPage />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/team"
                        element={
                          <ProtectedRoute>
                            <AdminRoute>
                              <AppLayout>
                                <TeamPage />
                              </AppLayout>
                            </AdminRoute>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/settings"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <SettingsPage />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </AuthProvider>
                </TenantGate>
              }
            />
          </Routes>
        </BrowserRouter>
      </TenantProvider>
    </SWRConfig>
  );
}

export default function App() {
  // Check if we're on the registration subdomain (app.kollektive.ai)
  // This bypasses tenant context and only allows registration routes
  if (isRegistrationSubdomain()) {
    return <RegistrationApp />;
  }

  return <TenantApp />;
}
